import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import { observer } from 'mobx-react-lite';
import { useNavigate } from 'react-router-dom';
import {
  Button,
  CheckboxField,
  FieldGroup,
  RadioField,
  SavingIndicator,
  TextField,
  WidgetFooter,
} from '../../../../components';
import { DeleteButton } from '../../../../components/controls/buttons';
import { useStore } from '../../../../store';
import { UserRoleOptions } from '../../../../store/constants/index';
import { onError, onSuccess } from '../../../../utils/errors';
import User from '../../store/User';

export default observer(function AccountWidget({ user }: { user: User }) {
  const { authStore, userStore } = useStore();
  const navigate = useNavigate();

  async function onImpersonateClick() {
    await authStore.impersonate(user.id);
    window.location.href = '/';
  }

  async function onDeleteClick() {
    try {
      await userStore.deleteUser(user);
      onSuccess(`${user.displayName} has been deleted`);
      navigate('/users');
    } catch (e) {
      onError(e, 'Cannot delete the user');
    }
  }

  return (
    <>
      {user.disabled && (
        <Chip
          color="warning"
          label="Disabled"
          size="small"
          title="The user cannot sign in"
          variant="outlined"
        />
      )}

      <FieldGroup sx={{ alignItems: 'flex-start', gap: 3 }}>
        <Box sx={{ display: 'flex', flex: 1, flexDirection: 'column' }}>
          <TextField disabled label="Email" value={user.email} />
          <RadioField
            label="Role"
            name="role"
            onChange={user.handleChange}
            options={UserRoleOptions}
            value={user.role}
          />
        </Box>

        <Box sx={{ display: 'flex', flex: 1, flexDirection: 'column' }}>
          <TextField disabled label="User ID" value={user.id} />
          {!user.isCustomer && (
            <>
              <TextField
                label="Developer Name"
                name="name"
                onChange={user.handleChange}
                value={user.name}
              />
              <TextField
                endAdornment="%"
                label="Price percentage per key"
                name="pricePercentagePerKey"
                onChange={user.configurations.handleChange}
                value={user.configurations.pricePercentagePerKey}
              />
            </>
          )}
        </Box>
      </FieldGroup>
      <CheckboxField
        helperText="The user won't be able to sign in"
        label="Disable account"
        name="disabled"
        onChange={user.handleChange}
        value={user.disabled}
      />
      {user.isProvider && (
        <>
          <CheckboxField
            label="Enable webhooks"
            name="isWebhookEnabled"
            onChange={user.configurations.handleChange}
            value={user.configurations.isWebhookEnabled}
          />
          <CheckboxField
            disabled={!user.configurations.isWebhookEnabled}
            helperText="When enabled, the webhook skips all orders. WARNING: order data will be lost."
            label="Skip webhook orders"
            name="skipWebhookOrders"
            onChange={user.configurations.handleChange}
            sx={{ ml: 4, mt: 1 }}
            value={user.configurations.skipWebhookOrders}
          />
          <CheckboxField
            helperText="Pulse Client won't be able to use keys of this provider"
            label="Disable Pulse Keys"
            name="disablePulseKeys"
            onChange={user.configurations.handleChange}
            value={user.configurations.disablePulseKeys}
          />
          <CheckboxField
            helperText="Use only PulseCDN and hide other storage options"
            label="Managed storage"
            name="managedStorage"
            onChange={user.configurations.handleChange}
            value={user.configurations.managedStorage}
          />
        </>
      )}

      <WidgetFooter>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          <Button
            disabled={user.hasErrors}
            disabledTooltip={user.allErrorsAsStrings}
            icon="save"
            onClick={() => user.save()}
            size="small"
            type="primary"
          >
            Save
          </Button>

          {!user.isCustomer && (
            <Button icon="user" onClick={onImpersonateClick} size="small">
              Impersonate
            </Button>
          )}

          {user.isCustomer && (
            <DeleteButton
              confirm={
                <>
                  <b>{user.displayName}</b> will be removed from the database
                  and from Firebase Auth. The Pulse keys stay, but are no longer
                  linked to anyone. This cannot be undone.
                </>
              }
              confirmTitle="Delete the user?"
              onClick={onDeleteClick}
            >
              Delete
            </DeleteButton>
          )}
        </Box>

        <SavingIndicator indicator={user.indicator} sx={{ ml: 2 }} />
      </WidgetFooter>
    </>
  );
});
